// tickets-csv-export (design D6, spec "Ticket Export Specification") —
// useTicketExport
//
// Drives the "Export to CSV" flow on TicketBrowsePage:
//   1. `startExport(filters)` — POSTs the active browse filters via
//      createTicketExport(), snapshotting the org-scoped export job.
//   2. Once a jobId is returned, polls getTicketExport(jobId) via
//      useQuery's `refetchInterval` until the job reaches a terminal state
//      (done|failed) — polling stops automatically at that point (spec:
//      one active export per org; UI does not need to keep hammering the
//      server after completion).
//   3. Surfaces a single `state` discriminated union so the page never has
//      to reconcile mutation + query loading/error flags itself.
//
// Filter → request mapping mirrors TicketBrowsePage's fetchPage() exactly
// (inclusive date-range window, empty string = omit).

import { createTicketExport, getTicketExport } from '@jq4race/api-client';
import { translateApiError, useTranslation } from '@jq4race/i18n';
import type { CreateTicketExportRequest, TicketExportJobResponse } from '@jq4race/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { TicketFilters } from '../components/TicketFilterBar';

const POLL_INTERVAL_MS = 2000;

export type TicketExportState =
  | { phase: 'idle' }
  | { phase: 'starting' }
  | { phase: 'pending'; jobId: string }
  | { phase: 'processing'; jobId: string }
  | { phase: 'done'; jobId: string; downloadUrl: string }
  | { phase: 'failed'; jobId: string; error: string }
  | { phase: 'error'; message: string };

export interface UseTicketExportResult {
  state: TicketExportState;
  startExport: (filters: TicketFilters) => void;
}

/** Mirrors TicketBrowsePage's fetchPage() filter → request-param conversion. */
export function filtersToExportRequest(filters: TicketFilters): CreateTicketExportRequest {
  const params: CreateTicketExportRequest = {};
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.status) params.status = filters.status;
  if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
  if (filters.from) params.from = `${filters.from}T00:00:00.000Z`;
  if (filters.to) params.to = `${filters.to}T23:59:59.999Z`;
  return params;
}

function isTerminal(status: TicketExportJobResponse['status'] | undefined): boolean {
  return status === 'done' || status === 'failed';
}

export function useTicketExport(): UseTicketExportResult {
  const { t, i18n } = useTranslation('tickets');
  // The file and the notification emails are rendered in the language the
  // requester is using right now (QA R2-09).
  const locale: 'en' | 'es' = (i18n.resolvedLanguage ?? i18n.language) === 'es' ? 'es' : 'en';
  const [jobId, setJobId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (filters: TicketFilters) =>
      createTicketExport({ ...filtersToExportRequest(filters), locale }),
    onSuccess: (res) => {
      setStartError(null);
      setJobId(res.jobId);
    },
    onError: (err: unknown) => {
      setJobId(null);
      setStartError(translateApiError(err, t, { fallback: t('export.startFailed') }));
    },
  });

  const statusQuery = useQuery<TicketExportJobResponse, Error>({
    queryKey: ['ticket-export', jobId],
    queryFn: () => getTicketExport(jobId as string),
    enabled: jobId !== null,
    // Stop polling once the job reaches a terminal state — TanStack Query
    // calls this on every refetch; returning `false` disables the interval.
    refetchInterval: (query) => (isTerminal(query.state.data?.status) ? false : POLL_INTERVAL_MS),
  });

  function startExport(filters: TicketFilters) {
    setJobId(null);
    setStartError(null);
    createMutation.mutate(filters);
  }

  const state: TicketExportState = (() => {
    if (startError) return { phase: 'error', message: startError };
    if (createMutation.isPending) return { phase: 'starting' };
    if (jobId && statusQuery.data) {
      const data = statusQuery.data;
      if (data.status === 'done') {
        return { phase: 'done', jobId, downloadUrl: data.downloadUrl ?? '' };
      }
      if (data.status === 'failed') {
        return { phase: 'failed', jobId, error: data.error ?? 'Export failed' };
      }
      return { phase: data.status, jobId };
    }
    if (jobId) return { phase: 'pending', jobId };
    return { phase: 'idle' };
  })();

  return { state, startExport };
}
